import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Select from '../../Select';
import * as defaultFormatters from '../formatters';
import translations from './translations';

const Filter = ({
  filter,
  filterOptions,
  formatters: providedFormatters,
  inline,
  isEditing,
  margin,
  onChange,
  onFetchOperators,
  onFetchFormat,
  onFetchSuggestions,
  onFetchValueProps,
  readOnly,
  register: shouldRegister,
}) => {
  const intl = useIntl();
  const [operatorsSuggestions, setOperatorSuggestions] = useState([]);
  const [format, setFormat] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [valueProps, setValueProps] = useState({});
  const { key: filterKey, operator } = filter;
  const handlersRef = useRef({
    onChange,
    onFetchFormat,
    onFetchOperators,
    onFetchSuggestions,
    onFetchValueProps,
  });
  const operatorRef = useRef(operator);
  const filterKeyRef = useRef(filterKey);
  const formatters = { ...defaultFormatters.default, ...providedFormatters };

  useEffect(() => {
    handlersRef.current = {
      onChange,
      onFetchFormat,
      onFetchOperators,
      onFetchSuggestions,
      onFetchValueProps,
    };
    operatorRef.current = operator;
  });

  /**
   * Handle the on change event.
   * @param {String} name
   */
  const handleOnChange = useCallback(
    name => (event, newValue) => {
      if (handlersRef.current?.onChange) {
        const operatorRequiresArray = ['in', 'not_in', 'between'].includes(operatorRef.current);
        const isArrayValue = Array.isArray(newValue);
        const convertToArray = name === 'values' && operatorRequiresArray && !isArrayValue;

        handlersRef.current.onChange(name, event, convertToArray ? [newValue] : newValue);
      }
    },
    [],
  );

  useEffect(() => {
    const handleFetchOperators = async () => {
      try {
        const { onFetchOperators: handleOperators } = handlersRef.current;

        if (handleOperators) {
          const operators = await handleOperators(filterKey);
          setOperatorSuggestions(operators);

          const operatorExists = operators.some(
            ({ value: operatorValue }) => operatorValue === operatorRef.current,
          );

          if (!operatorExists) {
            handleOnChange('operator')(null, '');
          }

          if (operators?.length === 1) {
            const [firstOperator] = operators;
            handleOnChange('operator')(null, firstOperator?.value ?? '');
          }
        }
      } catch (err) {
        setOperatorSuggestions([]);
        handleOnChange('operator')(null, '');
      }
    };

    const handleFetchFormat = async () => {
      try {
        const { onFetchFormat: handleFormat } = handlersRef.current;

        if (handleFormat) {
          const filterFormat = await handleFormat(filterKey);
          setFormat(filterFormat ?? null);
        }
      } catch (err) {
        setFormat(null);
      }
    };

    const handleFetchSuggestions = async () => {
      try {
        const { onFetchSuggestions: handleSuggestions } = handlersRef.current;

        if (handleSuggestions) {
          const filterSuggestions = await handleSuggestions(filterKey);
          setSuggestions(filterSuggestions ?? []);
        }
      } catch (err) {
        setSuggestions([]);
      }
    };

    const handleFetchValueProps = async () => {
      try {
        const { onFetchValueProps: handleValueProps } = handlersRef.current;

        if (handleValueProps) {
          const elementProps = await handleValueProps(filterKey);
          setValueProps(elementProps ?? {});
        }
      } catch (err) {
        setValueProps({});
      }
    };

    if (filterKey) {
      handleFetchFormat();
      handleFetchOperators();
      handleFetchSuggestions();
      handleFetchValueProps();

      if (filterKey !== filterKeyRef?.current) {
        handleOnChange('values')(null, []);
      }
    }
  }, [filterKey, handleOnChange]);

  useEffect(() => {
    filterKeyRef.current = filterKey;
  });

  const FilterElement = formatters[format] ?? formatters.default;

  return (
    <>
      <Select
        name="key"
        margin={margin}
        label={intl.formatMessage(translations.fields.type.label)}
        register={shouldRegister}
        suggestions={filterOptions}
        value={filter.key}
        onChange={handleOnChange('key')}
        inline={inline}
        isEditing={isEditing}
        readOnly={readOnly}
      />
      {operatorsSuggestions?.length > 1 && (
        <Select
          name="operator"
          margin={margin}
          label={intl.formatMessage(translations.fields.operator.label)}
          register={shouldRegister}
          value={operator}
          onChange={handleOnChange('operator')}
          suggestions={operatorsSuggestions}
          disabled={!filter.key}
          inline={inline}
          isEditing={isEditing}
          readOnly={readOnly}
        />
      )}
      {operator && operator !== 'missing' && (
        <FilterElement
          name="values"
          margin={margin}
          label={intl.formatMessage(translations.fields.value.label)}
          value={filter.values}
          onChange={handleOnChange('values')}
          operator={operator}
          suggestions={suggestions}
          disabled={!operator}
          register={shouldRegister}
          inline={inline}
          isEditing={isEditing}
          readOnly={readOnly}
          {...valueProps}
        />
      )}
    </>
  );
};

Filter.propTypes = {
  filter: PropTypes.object.isRequired,
  filterOptions: PropTypes.array,
  formatters: PropTypes.object,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  margin: PropTypes.string,
  onChange: PropTypes.func,
  onFetchFormat: PropTypes.func,
  onFetchOperators: PropTypes.func,
  onFetchSuggestions: PropTypes.func,
  onFetchValueProps: PropTypes.func,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
};

Filter.defaultProps = {
  filterOptions: [],
  formatters: {},
  inline: false,
  isEditing: false,
  margin: undefined,
  onChange: undefined,
  onFetchFormat: undefined,
  onFetchOperators: undefined,
  onFetchSuggestions: undefined,
  onFetchValueProps: undefined,
  readOnly: false,
  register: true,
};

export default Filter;
